// src/contexts/AuthContext.js
import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authService } from '../services/authService';

export const AuthContext = createContext();

const initialState = {
    user: null,
    token: null,
    isAuthenticated: false,
    loading: true,
    error: null
};

const authReducer = (state, action) => {
    switch (action.type) {
        case 'AUTH_START':
            return {
                ...state,
                loading: true,
                error: null
            };
        case 'AUTH_SUCCESS':
            return {
                ...state,
                user: action.payload.user,
                token: action.payload.token,
                isAuthenticated: true,
                loading: false,
                error: null
            };
        case 'REGISTER_SUCCESS':
            return {
                ...state,
                loading: false,
                error: null
            };
        case 'AUTH_FAILURE':
            return {
                ...state,
                user: null,
                token: null,
                isAuthenticated: false,
                loading: false,
                error: action.payload
            };
        case 'LOGOUT':
            return {
                ...initialState,
                loading: false
            };
        case 'CLEAR_ERROR':
            return {
                ...state,
                error: null
            };
        case 'SET_LOADING':
            return {
                ...state,
                loading: action.payload
            };
        default:
            return state;
    }
};

export const AuthProvider = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState);

    // Check for existing token on app load
    useEffect(() => {
        const token = localStorage.getItem('haazri_token');
        const user = localStorage.getItem('haazri_user');

        if (token && user) {
            try {
                const parsedUser = JSON.parse(user);
                // Verify token is still valid
                verifyToken(token, parsedUser);
            } catch (error) {
                localStorage.removeItem('haazri_token');
                localStorage.removeItem('haazri_user');
                dispatch({ type: 'SET_LOADING', payload: false });
            }
        } else {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, []);

    // Verify token validity
    const verifyToken = async (token, user) => {
        try {
            const response = await authService.verifyToken(token);
            if (response.valid) {
                dispatch({
                    type: 'AUTH_SUCCESS',
                    payload: { user, token }
                });
            } else {
                localStorage.removeItem('haazri_token');
                localStorage.removeItem('haazri_user');
                dispatch({ type: 'SET_LOADING', payload: false });
            }
        } catch (error) {
            // Network error, assume token is valid for now
            dispatch({
                type: 'AUTH_SUCCESS',
                payload: { user, token }
            });
        }
    };

    const login = async (username, password) => {
        dispatch({ type: 'AUTH_START' });

        try {
            const response = await authService.login(username, password);

            // Store token and user data
            localStorage.setItem('haazri_token', response.token);
            localStorage.setItem('haazri_user', JSON.stringify(response.user));

            dispatch({
                type: 'AUTH_SUCCESS',
                payload: {
                    user: response.user,
                    token: response.token
                }
            });

            return response;
        } catch (error) {
            dispatch({
                type: 'AUTH_FAILURE',
                payload: error.message || 'Login failed'
            });
            throw error;
        }
    };

    const register = async (orgData) => {
        dispatch({ type: 'AUTH_START' });

        try {
            const response = await authService.register(orgData);
            dispatch({ type: 'REGISTER_SUCCESS' });

            // This should return the success structure
            return {
                success: true,
                verification_required: true, // Always true for new registrations
                org_id: response.org_id,
                user_id: response.user_id,
                message: 'Registration successful! Please check your email to verify your account.'
            };
        } catch (error) {
            dispatch({
                type: 'AUTH_FAILURE',
                payload: error.message || 'Registration failed'
            });
            throw error;
        }
    };

    const logout = () => {
        localStorage.removeItem('haazri_token');
        localStorage.removeItem('haazri_user');
        dispatch({ type: 'LOGOUT' });
    };

    const clearError = () => {
        dispatch({ type: 'CLEAR_ERROR' });
    };

    const updateUser = (userData) => {
        const updatedUser = { ...state.user, ...userData };
        localStorage.setItem('haazri_user', JSON.stringify(updatedUser));
        dispatch({
            type: 'AUTH_SUCCESS',
            payload: {
                user: updatedUser,
                token: state.token
            }
        });
    };

    // Password reset request
    const forgotPassword = async (email) => {
        dispatch({ type: 'AUTH_START' });

        try {
            const response = await authService.forgotPassword(email);
            dispatch({ type: 'SET_LOADING', payload: false });
            return {
                success: true,
                message: response.message
            };
        } catch (error) {
            dispatch({
                type: 'AUTH_FAILURE',
                payload: error.message || 'Password reset request failed'
            });
            throw error;
        }
    };

    // Reset password
    const resetPassword = async (token, password) => {
        dispatch({ type: 'AUTH_START' });

        try {
            const response = await authService.resetPassword(token, password);
            dispatch({ type: 'SET_LOADING', payload: false });
            return {
                success: true,
                message: response.message
            };
        } catch (error) {
            dispatch({
                type: 'AUTH_FAILURE',
                payload: error.message || 'Password reset failed'
            });
            throw error;
        }
    };

    // Resend verification email
    const resendVerification = async (email) => {
        dispatch({ type: 'AUTH_START' });

        try {
            const response = await authService.resendVerification(email);
            dispatch({ type: 'SET_LOADING', payload: false });
            return {
                success: true,
                message: response.message
            };
        } catch (error) {
            dispatch({
                type: 'AUTH_FAILURE',
                payload: error.message || 'Failed to resend verification email'
            });
            throw error;
        }
    };

    // Change password (authenticated user)
    const changePassword = async (currentPassword, newPassword) => {
        dispatch({ type: 'AUTH_START' });

        try {
            const response = await authService.changePassword(
                currentPassword,
                newPassword,
                state.token
            );
            dispatch({ type: 'SET_LOADING', payload: false });
            return {
                success: true,
                message: response.message
            };
        } catch (error) {
            dispatch({
                type: 'AUTH_FAILURE',
                payload: error.message || 'Password change failed'
            });
            throw error;
        }
    };

    // Update profile
    const updateProfile = async (profileData) => {
        dispatch({ type: 'AUTH_START' });

        try {
            const response = await authService.updateProfile(profileData, state.token);
            const updatedUser = { ...state.user, ...response.user };

            localStorage.setItem('haazri_user', JSON.stringify(updatedUser));

            dispatch({
                type: 'AUTH_SUCCESS',
                payload: {
                    user: updatedUser,
                    token: state.token
                }
            });

            return {
                success: true,
                user: updatedUser,
                message: response.message
            };
        } catch (error) {
            dispatch({
                type: 'AUTH_FAILURE',
                payload: error.message || 'Profile update failed'
            });
            throw error;
        }
    };

    // Helper function to check permissions
    const hasPermission = (permission) => {
        if (!state.user) return false;

        // Core admin delegates use assigned permissions
        if (state.user.user_type === 'core_admin' && state.user.role === 'core_delegate') {
            const delegatePerms = Array.isArray(state.user.permissions) ? state.user.permissions : [];
            return delegatePerms.includes('*') ||
                delegatePerms.includes(permission) ||
                delegatePerms.includes(permission.split('.')[0] + '.*');
        }

        // Super core admin has all permissions
        if (state.user.user_type === 'core_admin') return true;

        // Define permission mappings
        const permissions = {
            'admin': ['*'], // All permissions
            'client_admin': [
                'staff.*', 'attendance.*', 'reports.*', 'settings.*',
                'leaves.*', 'bulk.*', 'premises.*', 'organizations.view'
            ],
            'app_admin': [
                'staff.view', 'staff.create', 'attendance.*', 'reports.view',
                'leaves.view', 'leaves.approve', 'premises.view'
            ],
            'staff': ['attendance.mark', 'leaves.apply', 'profile.view', 'profile.edit']
        };

        const userPerms = permissions[state.user.user_type] || [];

        return userPerms.includes('*') ||
            userPerms.includes(permission) ||
            userPerms.includes(permission.split('.')[0] + '.*');
    };

    // Check if user belongs to organization
    const belongsToOrganization = (orgId) => {
        return state.user && state.user.org_id === orgId;
    };

    // Check if user can access premises
    const canAccessPremises = (premisesId) => {
        if (!state.user) return false;

        // Core and client admins can access all premises in their org
        if (['core_admin', 'client_admin'].includes(state.user.user_type)) {
            return true;
        }

        // App admins and staff can only access their assigned premises
        return state.user.premises_id === premisesId;
    };

    // API call helper with authentication
    const apiCall = async (endpoint, options = {}) => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                ...(state.token && { 'Authorization': `Bearer ${state.token}` }),
                ...options.headers
            },
            ...options
        };

        try {
            const baseUrl = process.env.REACT_APP_API_URL || '/api';
            const response = await fetch(`${baseUrl}${endpoint}`, config);

            if (response.status === 401) {
                // Token expired or invalid
                logout();
                throw new Error('Session expired. Please log in again.');
            }

            const data = await response.json();

            if (response.status === 403) {
                const errorCode = data?.data?.error_code;

                if (['ORG_SUSPENDED', 'PREMISES_DEACTIVATED', 'LICENSE_EXPIRED'].includes(errorCode)) {
                    sessionStorage.setItem('haazri_logout_message', data.message || 'Your session has ended. Please contact support.');
                    logout();
                    throw new Error(data.message || 'Your session has ended. Please contact support.');
                }

                if (errorCode === 'LICENSE_EXPIRED_RENEWAL_ONLY') {
                    sessionStorage.setItem('haazri_renewal_message', data.message || "Your organization's subscription has expired. Please renew to continue.");
                    throw new Error(data.message || "Your organization's subscription has expired. Please renew to continue.");
                }
            }

            if (!response.ok) {
                throw new Error(data.message || 'API call failed');
            }

            return data;
        } catch (error) {
            throw error;
        }
    };

    const value = {
        // State
        ...state,

        // Authentication methods
        login,
        register,
        logout,
        clearError,
        updateUser,

        // Password management
        forgotPassword,
        resetPassword,
        changePassword,

        // Email verification
        resendVerification,

        // Profile management
        updateProfile,

        // Permission and access control
        hasPermission,
        belongsToOrganization,
        canAccessPremises,

        // API helper
        apiCall
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
